import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { randomValue } from 'srcReact/helpers/utils'
import { organizationActions } from 'srcReact/OrganizationPage'
import { alertActions } from 'srcReact/Alert'
import PageContainer from 'srcReact/PageContainer/PageContainer'
import RequiredStar from 'srcReact/components/common2/RequiredStar'
import { isEmail, isEmpty } from 'srcReact/helpers/validator'
import styles from './styles.module.scss'
import Button from 'srcReact/components/common2/Button'
import NotAuthorizedError from 'srcReact/components/common2/NotAuthorizedError'
import { Tip } from 'srcReact/components/common2/Tip'
import Spinner from 'srcReact/components/common2/Spinner'
import { TextBox } from 'srcReact/components/common2/TextBox'
import AnchorLink from 'srcReact/components/common2/AnchorLink'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import ImageCropper from 'srcReact/components/common2/ImageCropper'

function CreateOrganization() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const organizationColors = ['red', 'blue', 'orange', 'violet']
  const [color, setColor] = useState(randomValue(organizationColors))
  const [submitted, setSubmitted] = useState(false)
  const [enableButton, setEnableButton] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [createOrgClicked, setCreateOrgClicked] = useState(false)
  const [createOrgError, setCreateOrgError] = useState('')
  const [logoError, setLogoError] = useState('')
  const [organization, setOrganization] = useState({
    name: {
      value: '',
      placeholder: t('organization.create.placeholder_name'),
      hasError: false,
      errorMessage: '',
      isRequired: true
    },
    org_logo: {
      value: null,
      placeholder: t('organization.create.placeholder_img'),
      hasError: false,
      errorMessage: '',
      isRequired: true
    }
  })
  const {
    common: {
      isNative,
      isMobile,
      fileCreation: { files: uploadedFiles, loadingFile, error }
    },
    login: { user },
    organization: { header, new_orga }
  } = useSelector((state) => state)

  useEffect(() => {
    if (new_orga?.error && createOrgClicked) {
      setCreateOrgError(new_orga.error)
    }
  }, [new_orga])

  const handleChange = (e) => {
    if (submitted) {
      setSubmitted(false)
    }
    const { name, value } = e.target

    setOrganization({
      ...organization,
      [name]: {
        ...organization[name],
        value: value,
        hasError: false
      }
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setCreateOrgClicked(true)
    dispatch(alertActions.clear())
    setSubmitted(true)

    if (validate(organization)) {
      dispatch(organizationActions.create(organization.name.value, organization.org_logo.value?.id, color))
    }
  }

  const handleBlur = (e) => {
    if (e && e.target) {
      const { name, value } = e.target
      if (!isFieldValid(organization, name)) {
        setOrganization({
          ...organization,
          [name]: {
            ...organization[name],
            value: value,
            hasError: true
          }
        })
      }
    }
  }

  const validate = (org) => {
    let isValid = true
    const objToValidate = { ...org }

    for (const key in org) {
      if (!isFieldValid(objToValidate, key)) {
        isValid = false
      }
    }
    setOrganization({
      ...objToValidate
    })

    return isValid
  }
  const isFieldValid = (obj, key) => {
    if (obj[key].isRequired && isEmpty(obj[key].value)) {
      obj[key] = {
        ...obj[key],
        hasError: true,
        errorMessage: t('error.required', { field: t(`formlabel.${key}`) })
      }
      return false
    } else if (key === 'email' && !isEmail(obj[key].value, true)) {
      obj[key] = {
        ...obj[key],
        hasError: true,
        errorMessage: t('error.invalid_email', { field: t(`formlabel.${key}`) })
      }
      return false
    }
    return true
  }

  const handleCheckboxChange = (e) => {
    setEnableButton(e.target.checked)
    setTermsAccepted(e.target.checked)
  }

  const setOrgLogo = (picture) => {
    setOrganization({
      ...organization,
      org_logo: {
        ...organization.org_logo,
        value: picture,
        hasError: false
      }
    })
  }

  return (
    <React.Fragment>
      <PageContainer pageTitle={t('organization.create.pageTitle')} withFooter={false}>
        <div className={styles.createOrg}>
          {header?.user_properties?.organization.with_creation === 0 ? (
            <NotAuthorizedError />
          ) : (
            <div className={styles.wrapper}>
              <div className={styles.header}>
                <h1 className={styles.heading}>{t('organization.create.title')}</h1>
              </div>
              <div className={styles.body}>
                <div className={styles.backContainer}>
                  <AnchorLink
                    isNative={isNative}
                    aria-label={t('organization.create.backAlt')}
                    children={t('organization.create.back')}
                    icon={{
                      name: 'chevron-left',
                      position: 'start',
                      alt: '',
                      size: 'large',
                      styleModifier: styles.backLinkLeft
                    }}
                    path='/0/create-or-join-workplace'
                    className={styles.backLink}
                  />
                </div>
                <div className={styles.cardContainer}>
                  <div className={styles.cardOuter}>
                    <div className={styles.cardInner}>
                      <form name='form' className={styles.form} onSubmit={handleSubmit}>
                        <TextBox
                          label={t('organization.create.label_name')}
                          isRequired={organization['name'].isRequired}
                          customWrapperCss={`${styles.controlWrapper} ${styles.controlWrapperName}`}
                          customLabelCss={styles.formLabel}
                          customInputCss={styles.formInput}
                          customErrorCss={styles.formError}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name='name'
                          placeholder={organization['name'].placeholder}
                          fieldInfo={organization['name']}
                          maxLength={appConstantSettings.ORG_NAME_MAXLENGTH}
                        />
                        <div className={`${styles.controlWrapper} ${styles.controlWrapperLogo}`}>
                          <label className={styles.formLabel} htmlFor='name'>
                            {t('organization.create.label_img')}
                            {organization.org_logo.isRequired && <RequiredStar bold={true} />}
                          </label>

                          <ImageCropper
                            isMobile={isMobile}
                            recommendedFormatText={t('organization.create.helper_img')}
                            hideSkipButton={true}
                            aspectRatio={260 / 100}
                            exisingImageUrl={organization['org_logo'].value?.url}
                            showUploadZone={true}
                            handleNext={setOrgLogo}
                            containerCSS={styles.imageCropperContainer}
                            buttonWrapperCSS={styles.imageCropperActions}
                          />
                          {loadingFile && <Spinner />}
                          {logoError && logoError.length > 0 && <ErrorMessage text={logoError} />}

                          {submitted && organization['org_logo'].hasError && (
                            <div role='alert' className={styles.formError}>
                              {t('error.required', { field: 'Logo' })}
                            </div>
                          )}
                        </div>
                        <div className={styles.termsWrapper}>
                          <input
                            onChange={(e) => handleCheckboxChange(e)}
                            type='checkbox'
                            className={styles.termsCheckbox}
                            id='chkTerms'
                            checked={termsAccepted}
                          />
                          <label htmlFor='chkTerms' className={styles.termsLabel}>
                            <span className={styles.checkmark}></span>
                            <span className={styles.termsText}>
                              {t('organization.create.termsLink.agreeTo')}
                              <AnchorLink
                                isNative={isNative}
                                path='/0/organization-terms-of-services'
                                className={styles.termsLink}
                                children={t('organization.create.termsLink.terms')}
                              />
                            </span>
                          </label>
                        </div>

                        <div className={styles.buttonWrapper}>
                          <Button
                            disabled={!enableButton || new_orga?.submitted || loadingFile}
                            customStyle={styles.createOrgButton}
                            text={t('organization.create.submit')}
                            type='submit'
                            style='primary'
                            size='large'
                          />
                        </div>
                        {new_orga?.submitted && <Spinner />}
                        {createOrgError && <ErrorMessage center={true} text={createOrgError} />}
                      </form>
                    </div>
                  </div>
                </div>
                <div className={styles.tipContainer}>
                  <Tip position={isMobile ? 'bottom' : 'right'} text={t('organization.create.tip')} />
                </div>
              </div>
            </div>
          )}
        </div>
      </PageContainer>
    </React.Fragment>
  )
}
export { CreateOrganization }
